/**
 * Fully rewritten Tooltip system
 *
 * Features:
 * - Works like Tippy: trigger="mouseenter" | "click" | "focus" | "manual"
 * - Supports interactive tooltips (pointer events inside tooltip)
 * - Uses Floating UI for positioning + collision handling
 * - SSR‑safe, Next.js‑safe, Node‑24‑safe
 * - Allows HTML/content via tipChildren
 * - Allows underline mode for inline text
 */

import clsx from "clsx";
import * as React from "react";
import {
  useFloating,
  offset,
  flip,
  shift,
  autoUpdate,
  type Placement,
} from "@floating-ui/react-dom";

/* -------------------------------------------------------------------------- */
/*                                   TYPES                                    */
/* -------------------------------------------------------------------------- */

export type Position =
  | "top"
  | "top-start"
  | "top-end"
  | "bottom"
  | "bottom-start"
  | "bottom-end"
  | "left"
  | "left-start"
  | "left-end"
  | "right"
  | "right-start"
  | "right-end";

export type Trigger = "mouseenter" | "focus" | "click" | "manual";

export interface TooltipProps {
  /** Content displayed inside the tooltip bubble */
  tipChildren?: React.ReactNode;

  /** Element that receives hover/click/focus to trigger tooltip */
  children: React.ReactElement;

  /** Controlled open state (manual mode) */
  open?: boolean;

  /** Disable tooltip entirely */
  disabled?: boolean;

  /** Trigger mode (default: hover) */
  trigger?: Trigger;

  /** Tooltip placement relative to the reference element */
  position?: Position;

  /** Allow hovering inside tooltip */
  interactive?: boolean;

  /** Tooltip styling */
  className?: string;

  /** Inline style for tooltip container */
  style?: React.CSSProperties;

  /** Called when tooltip should close in manual or click mode */
  onRequestClose?: () => void;
}

/* -------------------------------------------------------------------------- */
/*                                COMPONENT                                    */
/* -------------------------------------------------------------------------- */

export const Tooltip: React.FC<TooltipProps> = ({
  children,
  tipChildren,
  trigger = "mouseenter",
  disabled = false,
  interactive = false,
  position = "top",
  open,
  className,
  style,
  onRequestClose,
}) => {
  const isControlled = open !== undefined;

  const [internalOpen, setInternalOpen] = React.useState(false);
  const visible = isControlled ? open : internalOpen;

  const {
    refs,
    floatingStyles,
  } = useFloating({
    placement: position as Placement,
    middleware: [offset(8), flip(), shift()],
    whileElementsMounted: autoUpdate,
  });

  /* -------------------------------------------------------------------------- */
  /*                         SHOW / HIDE HANDLERS                                */
  /* -------------------------------------------------------------------------- */

  const show = () => {
    if (disabled) return;
    if (!isControlled) setInternalOpen(true);
  };

  const hide = () => {
    if (isControlled) onRequestClose?.();
    else setInternalOpen(false);
  };

  /* -------------------------------------------------------------------------- */
  /*                    CHILD AUGMENTATION (ADD EVENTS)                          */
  /* -------------------------------------------------------------------------- */

  const child = React.cloneElement(children, {
    ref: refs.setReference,
    onMouseEnter:
      trigger === "mouseenter"
        ? (e: any) => {
          children.props.onMouseEnter?.(e);
          show();
        }
        : children.props.onMouseEnter,
    onMouseLeave:
      trigger === "mouseenter"
        ? (e: any) => {
          children.props.onMouseLeave?.(e);
          hide();
        }
        : children.props.onMouseLeave,
    onFocus:
      trigger === "focus"
        ? (e: any) => {
          children.props.onFocus?.(e);
          show();
        }
        : children.props.onFocus,
    onBlur:
      trigger === "focus"
        ? (e: any) => {
          children.props.onBlur?.(e);
          hide();
        }
        : children.props.onBlur,
    onClick:
      trigger === "click"
        ? (e: any) => {
          children.props.onClick?.(e);
          visible ? hide() : show();
        }
        : children.props.onClick,
  });

  /* -------------------------------------------------------------------------- */
  /*                                  RENDER                                     */
  /* -------------------------------------------------------------------------- */

  return (
    <>
      {child}

      {visible && tipChildren && (
        <div
          ref={refs.setFloating}
          style={{
            ...floatingStyles,
            ...style,
            pointerEvents: interactive ? "auto" : "none",
          }}
          className={clsx(
            "z-50 rounded-md border border-gray-300 bg-white px-3 py-2 shadow-lg",
            "dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600",
            className
          )}
        >
          {tipChildren}
        </div>
      )}
    </>
  );
};

/* -------------------------------------------------------------------------- */
/*                      FRIENDLY TEXT-TOOLTIP WRAPPER                           */
/* -------------------------------------------------------------------------- */

export type TooltipTextProps = {
  tipChildren?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  spanClassName?: string;
  withUnderline?: boolean;
} & Omit<TooltipProps, "children" | "tipChildren">;

/**
 * Wrapper around Tooltip for text/content tooltips.
 */
export default function Tooltipfunc({
  tipChildren,
  children,
  className,
  spanClassName,
  withUnderline = false,
  ...rest
}: TooltipTextProps) {
  return (
    <Tooltip
      trigger="mouseenter"
      tipChildren={
        <div
          className={clsx(
            className,
            "inline-block rounded-md bg-white p-2 text-gray-600 shadow-md",
            "dark:bg-dark dark:text-gray-200 dark:border-gray-600 border"
          )}
        >
          {tipChildren}
        </div>
      }
      {...rest}
    >
      {withUnderline ? (
        <span
          className={clsx(spanClassName, "underline")}
          style={{ textDecorationStyle: "dotted" }}
        >
          {children}
        </span>
      ) : (
        <>{children}</>
      )}
    </Tooltip>
  );
}
